<template>
  <div class="rounded-full border border-border">
    <div
      ref="track"
      class="group relative h-3 touch-none rounded-full outline-none"
      :class="$style.track"
      role="slider"
      tabindex="0"
      :aria-valuenow="Math.round(modelValue)"
      aria-valuemin="0"
      aria-valuemax="360"
      aria-label="Hue"
      @pointerdown="onPointerDown"
      @keydown="onKeydown"
    >
      <div
        class="pointer-events-none absolute top-1/2 size-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white group-focus-visible:size-6 group-active:size-6"
        :class="$style.thumb"
        :style="{
          left: `${(modelValue / 360) * 100}%`,
          backgroundColor: `hsl(${modelValue}, 100%, 50%)`,
        }"
      ></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTemplateRef } from "vue";

const modelValue = defineModel<number>({ required: true });

const track = useTemplateRef("track");

function hueFromPointer(event: PointerEvent) {
  const rect = track.value!.getBoundingClientRect();
  const percent = Math.max(
    0,
    Math.min(1, (event.clientX - rect.left) / rect.width)
  );
  return percent * 360;
}

function onPointerDown(event: PointerEvent) {
  if (event.button !== 0) {
    return;
  }

  event.preventDefault();
  (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);

  modelValue.value = hueFromPointer(event);

  const onPointerMove = (e: PointerEvent) => {
    modelValue.value = hueFromPointer(e);
  };

  const onPointerUp = (e: PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    track.value!.removeEventListener("pointermove", onPointerMove);
    track.value!.removeEventListener("pointerup", onPointerUp);
  };

  track.value!.addEventListener("pointermove", onPointerMove);
  track.value!.addEventListener("pointerup", onPointerUp);
}

function onKeydown(event: KeyboardEvent) {
  let delta = 0;

  switch (event.key) {
    case "ArrowLeft":
      delta = -1;
      break;
    case "ArrowRight":
      delta = 1;
      break;
    case "ArrowUp":
      delta = 10;
      break;
    case "ArrowDown":
      delta = -10;
      break;
    case "Home":
      modelValue.value = 0;
      return;
    case "End":
      modelValue.value = 360;
      return;
    default:
      return;
  }

  event.preventDefault();
  modelValue.value = Math.max(0, Math.min(360, modelValue.value + delta));
}
</script>

<style module>
.track {
  background: linear-gradient(
    to right,
    #ff0000,
    #ffff00,
    #00ff00,
    #00ffff,
    #0000ff,
    #ff00ff,
    #ff0000
  );
}

.thumb {
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: width 0.15s, height 0.15s;
}
</style>
